import React, { useMemo, useState } from 'react';
import styled, { useTheme } from 'styled-components';
import { Ban, CheckCircle2, Eye, RefreshCw, Trash2 } from 'lucide-react';
import { backofficeApi, extractErrorMessage, formatNumber, formatDate, formatPhone } from '@kardume/services';
import { useNotification } from '@kardume/hooks';
import type { CustomerDto, CustomerStatus, Tone } from '@kardume/types';
import {
  PageHeader,
  StatCard,
  Table,
  TableContainer,
  Badge,
  Button,
  ActionButton,
  ConfirmModal,
  Avatar,
  SearchInput,
  Select,
} from '@kardume/ui';
import { useAsync } from '../utils/useAsync';
import DataState from '../components/DataState';
import CustomerDetailDrawer from '../components/CustomerDetailDrawer';

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 1rem;
  margin-bottom: 1.25rem;

  @media (max-width: 900px) {
    grid-template-columns: repeat(2, 1fr);
  }
`;

const Toolbar = styled.div`
  display: flex;
  gap: 0.75rem;
  margin-bottom: 1rem;
  flex-wrap: wrap;
  align-items: center;

  > *:first-child {
    flex: 1;
    min-width: 240px;
  }
`;

const Person = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;

  strong {
    display: block;
    color: ${(p) => p.theme.textPrimary};
  }
  .email {
    font-size: 0.82rem;
    color: ${(p) => p.theme.textMuted};
  }
  .id {
    font-family: monospace;
    font-size: 0.68rem;
    color: ${(p) => p.theme.textMuted};
    text-transform: uppercase;
  }
  .registered {
    font-size: 0.72rem;
    color: ${(p) => p.theme.textMuted};
  }
  /* Etiqueta de influenciador: linha própria, colada no nome. Ao lado do nome
     ela empurraria o e-mail e o ID para baixo em quem tem nome longo, e a
     coluna já é a mais cheia da tabela. */
  .tags {
    display: flex;
    gap: 0.3rem;
    margin-top: 0.25rem;
  }
`;

const DocCell = styled.div`
  white-space: nowrap;

  .k {
    font-size: 0.7rem;
    color: ${(p) => p.theme.textMuted};
  }
  strong {
    display: block;
    color: ${(p) => p.theme.textPrimary};
    white-space: nowrap;
  }
  span {
    font-size: 0.82rem;
    color: ${(p) => p.theme.textMuted};
    white-space: nowrap;
  }
`;

const RowActions = styled.div`
  display: flex;
  gap: 0.4rem;
  justify-content: flex-end;
  white-space: nowrap;
`;

const STATUS_LABELS: Record<CustomerStatus, string> = {
  PENDING_ACTIVATION: 'Pendente',
  ACTIVE: 'Ativo',
  INACTIVE: 'Inativo',
  BLOCKED: 'Bloqueado',
};

const STATUS_FILTER = [
  { value: '', label: 'Todos os status' },
  { value: 'ACTIVE', label: 'Ativo' },
  { value: 'PENDING_ACTIVATION', label: 'Pendente' },
  { value: 'INACTIVE', label: 'Inativo' },
  { value: 'BLOCKED', label: 'Bloqueado' },
];

function statusTone(status: CustomerStatus): Tone {
  if (status === 'ACTIVE') return 'success';
  if (status === 'PENDING_ACTIVATION') return 'warning';
  if (status === 'BLOCKED') return 'danger';
  return 'secondary';
}

const CustomersPage: React.FC = () => {
  const theme = useTheme();
  const { showNotification } = useNotification();
  const { data, loading, error, reload } = useAsync(() => backofficeApi.listCustomers({ size: 100 }), []);
  // Planos do Kardume Pass disponíveis - usados no filtro/coluna e na troca de
  // plano pelo drawer de detalhe (só planos ativos podem ser atribuídos).
  const { data: plansData } = useAsync(() => backofficeApi.listPassPlans({ size: 100 }), []);
  const plans = useMemo(() => (plansData?.content ?? []).filter((p) => p.active), [plansData]);

  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [pendingId, setPendingId] = useState<string | null>(null);
  const [planPendingId, setPlanPendingId] = useState<string | null>(null);
  const [detail, setDetail] = useState<CustomerDto | null>(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<CustomerDto | null>(null);
  const [deleting, setDeleting] = useState(false);

  const customers = useMemo<CustomerDto[]>(() => data?.content ?? [], [data]);

  const stats = useMemo(
    () => ({
      total: data?.totalElements ?? customers.length,
      active: customers.filter((c) => c.status === 'ACTIVE').length,
      pending: customers.filter((c) => c.status === 'PENDING_ACTIVATION').length,
      blocked: customers.filter((c) => c.status === 'BLOCKED').length,
    }),
    [data, customers]
  );

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    return customers.filter((c) => {
      const matchesStatus = !statusFilter || c.status === statusFilter;
      const matchesTerm =
        !term ||
        [`${c.firstName} ${c.lastName}`, c.email ?? '', c.document ?? '', c.phone ?? ''].some((v) =>
          v.toLowerCase().includes(term)
        );
      return matchesStatus && matchesTerm;
    });
  }, [customers, search, statusFilter]);

  const openDetail = (customer: CustomerDto) => {
    setDetail(customer);
    setDetailOpen(true);
  };

  /**
   * Exclusão do cliente. Bloquear e excluir resolvem coisas diferentes: bloquear
   * é reversível e mantém a pessoa na base; excluir tira das listas e encerra a
   * assinatura ativa (senão a cobrança seguiria todo mês). O histórico de compras
   * permanece - é o que sustenta o caixa do lojista.
   */
  const confirmDelete = async () => {
    if (!deleteTarget || deleting) return;
    setDeleting(true);
    try {
      await backofficeApi.deleteCustomer(deleteTarget.id);
      showNotification('Cliente excluído.', 'success');
      setDeleteTarget(null);
      reload();
    } catch (err) {
      showNotification(extractErrorMessage(err, 'Não foi possível excluir o cliente.'), 'error');
    } finally {
      setDeleting(false);
    }
  };

  const toggleStatus = async (c: CustomerDto) => {
    if (pendingId) return;
    const next: CustomerStatus = c.status === 'BLOCKED' ? 'ACTIVE' : 'BLOCKED';
    setPendingId(c.id);
    try {
      const updated = await backofficeApi.updateCustomerStatus(c.id, next);
      showNotification(next === 'BLOCKED' ? 'Usuário bloqueado.' : 'Usuário ativado.', 'success');
      setDetail((prev) => (prev && prev.id === updated.id ? updated : prev));
      await reload();
    } catch (err) {
      showNotification(extractErrorMessage(err, 'Não foi possível atualizar o status.'), 'error');
    } finally {
      setPendingId(null);
    }
  };

  const changePlan = async (c: CustomerDto, planId: string) => {
    if (planPendingId) return;
    setPlanPendingId(c.id);
    try {
      const updated = await backofficeApi.updateCustomerPlan(c.id, planId);
      showNotification('Plano do cliente atualizado.', 'success');
      setDetail((prev) => (prev && prev.id === updated.id ? updated : prev));
      await reload();
    } catch (err) {
      showNotification(extractErrorMessage(err, 'Não foi possível atualizar o plano.'), 'error');
    } finally {
      setPlanPendingId(null);
    }
  };

  return (
    <>
      <PageHeader title="Usuários" subtitle="Consumidores cadastrados na plataforma Kardume Urbano." />

      <StatsGrid>
        <StatCard label="Total de usuários" value={formatNumber(stats.total)} />
        <StatCard label="Ativos" value={formatNumber(stats.active)} />
        <StatCard label="Pendentes" value={formatNumber(stats.pending)} />
        <StatCard label="Bloqueados" value={formatNumber(stats.blocked)} />
      </StatsGrid>

      <Toolbar>
        <SearchInput
          placeholder="Buscar por nome, e-mail, documento ou telefone..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <Select options={STATUS_FILTER} value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} />
        <ActionButton title="Atualizar" onClick={reload}>
          <RefreshCw size={16} />
        </ActionButton>
      </Toolbar>

      <DataState
        loading={loading}
        error={error}
        onRetry={reload}
        isEmpty={filtered.length === 0}
        emptyTitle="Nenhum usuário encontrado"
        emptyDescription="Ajuste os filtros para ver os consumidores cadastrados."
      >
        <TableContainer>
          <Table>
            <thead>
              <tr>
                <th>Usuário</th>
                <th style={{ width: '160px', textAlign: 'right', whiteSpace: 'nowrap' }}>Documento</th>
                <th style={{ width: '110px', whiteSpace: 'nowrap' }}>Plano</th>
                <th style={{ width: '70px', textAlign: 'right', whiteSpace: 'nowrap' }}>Status</th>
                <th style={{ width: '140px', textAlign: 'right', whiteSpace: 'nowrap' }}>Ações</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((c) => {
                const isBlocked = c.status === 'BLOCKED';
                return (
                  <tr key={c.id}>
                    <td>
                      <Person>
                        <Avatar name={`${c.firstName} ${c.lastName}`} src={c.imageUrl ?? undefined} />
                        <div>
                          <strong>
                            {c.firstName} {c.lastName}
                          </strong>
                          <div className="email">{c.email ?? '-'}</div>
                          <div className="id">ID: {c.id.slice(0, 8)}...</div>
                          <div className="registered">Cadastro: {formatDate(c.createdAt)}</div>
                          {/* Spec 038 - quem tem contrato de influenciador aparece
                              marcado aqui, para a operação não precisar abrir
                              Financeiro para descobrir. Contrato pausado não some
                              da lista: ele ainda deve o que foi apurado, e sumir
                              faria parecer que nunca existiu. */}
                          {c.influencer && (
                            <div className="tags">
                              <Badge tone={c.influencerActive ? 'primary' : 'secondary'}>
                                {c.influencerActive ? 'Influenciador' : 'Influenciador pausado'}
                              </Badge>
                            </div>
                          )}
                        </div>
                      </Person>
                    </td>
                    <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                      <DocCell>
                        <strong>{c.formattedDocument ?? c.document ?? '-'}</strong>
                        {c.phone && <span>{formatPhone(c.phone)}</span>}
                      </DocCell>
                    </td>
                    <td style={{ maxWidth: '110px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {c.passPlanName ? (
                        <span title={c.passPlanName}>
                          <Badge tone={c.passPlanPrice && c.passPlanPrice > 0 ? 'primary' : 'secondary'}>
                            {c.passPlanName}
                          </Badge>
                        </span>
                      ) : (
                        <Badge tone="warning">Sem plano</Badge>
                      )}
                    </td>
                    <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                      <Badge tone={statusTone(c.status)}>{STATUS_LABELS[c.status]}</Badge>
                    </td>
                    <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                      <RowActions>
                        <ActionButton title="Ver detalhes" onClick={() => openDetail(c)}>
                          <Eye size={16} />
                        </ActionButton>
                        <Button
                          variant={isBlocked ? 'outline' : 'danger'}
                          isLoading={pendingId === c.id}
                          onClick={() => toggleStatus(c)}
                        >
                          {isBlocked ? (
                            <>
                              <CheckCircle2 size={16} /> Ativar
                            </>
                          ) : (
                            <>
                              <Ban size={16} /> Bloquear
                            </>
                          )}
                        </Button>
                        <ActionButton title="Excluir" color={theme.error} onClick={() => setDeleteTarget(c)}>
                          <Trash2 size={16} />
                        </ActionButton>
                      </RowActions>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </Table>
        </TableContainer>
      </DataState>

      <ConfirmModal
        isOpen={!!deleteTarget}
        danger
        title="Excluir cliente"
        message={
          <>
            Excluir <strong>{deleteTarget?.firstName} {deleteTarget?.lastName}</strong>? A assinatura
            ativa é encerrada e o cliente sai das listas. O histórico de compras é preservado.
          </>
        }
        confirmLabel="Excluir"
        loading={deleting}
        onConfirm={confirmDelete}
        onClose={() => setDeleteTarget(null)}
      />

      <CustomerDetailDrawer
        customer={detail}
        isOpen={detailOpen}
        pending={pendingId === detail?.id}
        plans={plans}
        planPending={planPendingId === detail?.id}
        onClose={() => setDetailOpen(false)}
        onToggleStatus={toggleStatus}
        onChangePlan={changePlan}
      />
    </>
  );
};

export default CustomersPage;
